import { Minus, Plus } from 'lucide-react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { useT } from '../lib/i18n'
import { parseDecimal } from './NumberInput'

interface StepperProps {
  label: string
  value: number
  onChange: (value: number) => void
  step?: number
  min?: number
  max?: number
  unit?: string
  /** Decimal places the value is rounded to. Portions are usually 0 or 1. */
  precision?: number
}

/** Hold-to-repeat, in milliseconds. */
const HOLD_DELAY = 400
const REPEAT_SLOW = 125 // 8/s
const REPEAT_FAST = 50 // 20/s
const ACCELERATE_AFTER = 2000

/**
 * The portion control.
 *
 * This one gets more care than its size suggests, because portion editing is
 * the flow the whole product is timed on: `SUAP-0403` puts a 15-second budget on
 * logging a repeat meal, and most of those seconds are spent here. So:
 *
 * - **Hold to repeat.** Press and hold either button: one step immediately, a
 *   400 ms pause (so a tap is a tap), then 8 steps a second, accelerating to 20
 *   after two seconds. Going from 100 g to 250 g is a hold, not fifteen taps.
 * - **Typed entry in the middle.** The value is an input, not a label. Someone
 *   who knows it is 175 g types 175. A comma is a decimal separator, as
 *   everywhere else in this app.
 * - **Arrow keys, PageUp/PageDown** on the field: ±1 step and ±10 steps.
 *
 * The repeat timer is cleared on `pointerup`, `pointercancel` and on unmount. A
 * timer that survives its component is how a phone gets warm in a pocket, and it
 * is the defect this component is most likely to ship.
 *
 * It is **not** cleared when the finger drifts off the button, and that is
 * deliberate: `setPointerCapture` retains the pointer, so a hold that slides
 * sideways keeps repeating and still stops on release. The `onPointerLeave`
 * handler is therefore belt-and-braces for the case where capture fails. (An
 * earlier version of this comment claimed leaving the button stopped the repeat.
 * A review measured it climbing 170 → 200 while dragged 200px away. The
 * behaviour is right; the sentence was wrong.)
 *
 * SUAP-0103.
 */
export function Stepper({
  label,
  value,
  onChange,
  step = 1,
  min = 0,
  max = 9999,
  unit,
  precision = 0,
}: StepperProps) {
  const id = useId()
  const { t } = useT()
  const [draft, setDraft] = useState<string | null>(null)

  // The repeat callback closes over these; refs keep it reading the live values
  // instead of the ones captured when the press started.
  const valueRef = useRef(value)
  valueRef.current = value
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const timers = useRef<{ delay?: ReturnType<typeof setTimeout>; repeat?: ReturnType<typeof setInterval>; accelerate?: ReturnType<typeof setTimeout> }>({})

  const clamp = useCallback(
    (next: number) => {
      const bounded = Math.min(max, Math.max(min, next))
      return Number(bounded.toFixed(precision))
    },
    [max, min, precision],
  )

  const nudge = useCallback(
    (direction: 1 | -1, multiplier = 1) => {
      const next = clamp(valueRef.current + direction * step * multiplier)
      if (next !== valueRef.current) onChangeRef.current(next)
    },
    [clamp, step],
  )

  const stopRepeat = useCallback(() => {
    clearTimeout(timers.current.delay)
    clearTimeout(timers.current.accelerate)
    clearInterval(timers.current.repeat)
    timers.current = {}
  }, [])

  const startRepeat = useCallback(
    (direction: 1 | -1) => {
      stopRepeat()
      nudge(direction)
      timers.current.delay = setTimeout(() => {
        timers.current.repeat = setInterval(() => nudge(direction), REPEAT_SLOW)
        timers.current.accelerate = setTimeout(() => {
          clearInterval(timers.current.repeat)
          timers.current.repeat = setInterval(() => nudge(direction), REPEAT_FAST)
        }, ACCELERATE_AFTER)
      }, HOLD_DELAY)
    },
    [nudge, stopRepeat],
  )

  useEffect(() => stopRepeat, [stopRepeat])

  const atMin = value <= min
  const atMax = value >= max

  return (
    // `relative` is load-bearing, not decorative. The `sr-only` live region below
    // is `position: absolute`, and with no positioned ancestor its containing
    // block was `PhoneFrame` — so its static position landed ~1000px down and
    // gave the column 179px of hidden scrollable height. `overflow: hidden` stops
    // a user scrolling that, but not `scrollIntoView`, and `#overlay-root` is
    // `absolute inset-0` of the frame: one programmatic scroll lifted the scrim
    // and the bottom sheet 123px off the bottom of the column, unveiling the
    // content behind them. Found in review (SUAP-0103). Containing the span here
    // fixes it at the source.
    <div className="relative flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>

      <div
        role="group"
        aria-label={label}
        className="flex items-stretch gap-2 rounded-input border border-hairline bg-surface p-1 focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ink"
      >
        <RepeatButton
          label={t('stepper.decrease', { label })}
          disabled={atMin}
          onStart={() => startRepeat(-1)}
          onStop={stopRepeat}
        >
          <Minus aria-hidden size={20} />
        </RepeatButton>

        <input
          id={id}
          type="text"
          inputMode="decimal"
          value={draft ?? String(value)}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            const parsed = parseDecimal(draft ?? '')
            if (draft !== null && parsed !== null) onChange(clamp(parsed))
            setDraft(null)
          }}
          onKeyDown={(event) => {
            const moves: Record<string, [1 | -1, number]> = {
              ArrowUp: [1, 1],
              ArrowDown: [-1, 1],
              PageUp: [1, 10],
              PageDown: [-1, 10],
            }
            const move = moves[event.key]
            if (move !== undefined) {
              event.preventDefault()
              setDraft(null)
              nudge(move[0], move[1])
              return
            }
            if (event.key === 'Enter') event.currentTarget.blur()
          }}
          className="min-h-11 min-w-0 flex-1 self-stretch bg-transparent text-center text-lg font-semibold tabular-nums text-ink outline-none"
        />

        {unit === undefined ? null : (
          <span aria-hidden className="self-center pr-1 text-sm text-ink-soft">
            {unit}
          </span>
        )}

        <RepeatButton
          label={t('stepper.increase', { label })}
          disabled={atMax}
          onStart={() => startRepeat(1)}
          onStop={stopRepeat}
        >
          <Plus aria-hidden size={20} />
        </RepeatButton>
      </div>

      {/* The value as text, for a screen reader that will not read a changing
          input value on its own. Polite: a portion nudged ten times should not
          interrupt ten times. */}
      <span aria-live="polite" className="sr-only">
        {`${value}${unit === undefined ? '' : ` ${unit}`}`}
      </span>
    </div>
  )
}

interface RepeatButtonProps {
  label: string
  disabled: boolean
  onStart: () => void
  onStop: () => void
  children: React.ReactNode
}

function RepeatButton({ label, disabled, onStart, onStop, children }: RepeatButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onPointerDown={(event) => {
        // Keep the press even if the finger drifts off the button — the pointer
        // is captured, so pointerup still lands here and the repeat still stops.
        event.currentTarget.setPointerCapture(event.pointerId)
        onStart()
      }}
      onPointerUp={onStop}
      onPointerCancel={onStop}
      onPointerLeave={onStop}
      onBlur={onStop}
      className={[
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-input text-ink',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
        disabled ? 'opacity-40' : 'bg-card border border-hairline',
      ].join(' ')}
    >
      {children}
    </button>
  )
}
